import {
  AutocompleteBase,
  InteractableComponent,
  Text,
} from '@shared/components';
import { CreateCustomerLocation } from '@shared/constants/identifiers';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { useGlossary } from '@shared/hooks/useGlossary';
import { Order } from '@shared/types/order';
import {
  makeElementClassNameFactory,
  makeRootClassName,
  StyleProps,
} from '@shared/utils';
import { isNil } from 'lodash';
import { ReactElement } from 'react';
import { OriginatorSearch } from '../originator-search/OriginatorSearch';
import { OriginatorOption } from '../originator-search/getInitialOriginatorOption';

const ROOT = makeRootClassName('OriginatorDetails');
export const el = makeElementClassNameFactory(ROOT);

type OriginatorDetailsProps = StyleProps & {
  originator: Order['originator'];
  transmitId?: string;
  updateOriginator: (id: string) => void;
  updateTransmitId?: (id: string) => void;
  loading: boolean;
  originatorOptions: OriginatorOption[];
  customerDetailsCTA?: string;
  navigateToOriginatorDetails?: () => void;
  initiateOriginatorOnboarding?: (
    originatorName: string
  ) => Promise<string | undefined>;
  handleSearch: (query?: string) => void;
  hideCreateOption?: boolean;
};
export function OriginatorDetails({
  originator,
  transmitId,
  updateTransmitId,
  updateOriginator,
  loading,
  originatorOptions,
  customerDetailsCTA,
  navigateToOriginatorDetails,
  initiateOriginatorOnboarding,
  handleSearch,
  hideCreateOption,
}: OriginatorDetailsProps): ReactElement {
  const { track } = useAnalytics();
  const { customerCode } = useGlossary();

  const legacyTransmitIdOptions = originator.transmitIds.map((id) => ({
    id,
    label: id,
  }));
  const externalIdMappingOptions = (originator?.externalIdMappings ?? []).map(
    (mapping) => ({
      id: mapping.externalId,
      label: mapping.externalId,
    })
  );

  const transmitIdOptions = [
    ...legacyTransmitIdOptions,
    ...externalIdMappingOptions,
  ];

  const createOriginator = async (name: string) => {
    if (isNil(initiateOriginatorOnboarding)) return undefined;
    const originatorId = await initiateOriginatorOnboarding(name);
    track('Clicked Create Customer', {
      originatorName: name,
      originatorId,
      name: CreateCustomerLocation.ORDER_ORIGINATOR_DROPDOWN,
    });
    return originatorId;
  };

  return (
    <div className={el`originator-details`}>
      <Text type="body-md" isHeavy className={el`originator-details-title`}>
        Customer Details
      </Text>
      <div className={el`originator-field`}>
        <div className="flex w-full flex-grow flex-row gap-2">
          <OriginatorSearch
            originator={originator}
            updateOriginator={updateOriginator}
            loading={loading}
            originatorOptions={originatorOptions}
            handleSearch={handleSearch}
            createOriginator={createOriginator}
            hideCreateOption={hideCreateOption}
            originatorRequired={true}
          />
        </div>
        {originator.id && navigateToOriginatorDetails && (
          <OpenOriginatorDetailsButton
            navigateToOriginatorDetails={navigateToOriginatorDetails}
            customerDetailsCTA={customerDetailsCTA}
          />
        )}
      </div>
      {updateTransmitId && originator.transmitIds.length > 1 && (
        <AutocompleteBase
          label={customerCode}
          defaultValue={transmitId}
          className={el`transmit-id-field`}
          size="small"
          onChange={(_, value) => {
            if (!value) return;
            updateTransmitId((value as { id: string }).id);
          }}
          inputProps={{
            required: true,
          }}
          options={transmitIdOptions}
          isOptionEqualToValue={(option, value) => option.id === value?.id}
          isDisabled={loading}
          value={transmitId}
          blurOnSelect
        />
      )}
    </div>
  );
}

type OpenOriginatorDetailsButtonProps = {
  navigateToOriginatorDetails: () => void;
  customerDetailsCTA?: string;
};
export function OpenOriginatorDetailsButton({
  navigateToOriginatorDetails,
  customerDetailsCTA,
}: OpenOriginatorDetailsButtonProps) {
  return (
    <InteractableComponent onPress={navigateToOriginatorDetails}>
      <Text
        type="body-xs"
        className="text-brand-400 flex w-full cursor-pointer justify-end pt-[4px]"
      >
        {customerDetailsCTA || 'Open customer details'}
      </Text>
    </InteractableComponent>
  );
}
